import { Play, Square, Loader2 } from "lucide-react";
import { C, FONT, card, clamp, mmss } from "../theme";
import type { Zone } from "../types";
import { Reservoir } from "./Reservoir";
import { Pill } from "./Pill";
import { Stepper } from "./Stepper";
import { SourceDot } from "../lib/sources";

export function ZoneCard({
  z,
  paused,
  soilSrc,
  flowSrc,
  busyRun,
  busyStop,
  onRun,
  onStop,
  onStep,
  onToggle,
  onSelect,
}: {
  z: Zone;
  paused: boolean;
  soilSrc?: string;
  flowSrc?: string;
  busyRun?: boolean;
  busyStop?: boolean;
  onRun: (id: string, minutes: number) => void;
  onStop: (id: string) => void;
  onStep: (id: string, minutes: number) => void;
  onToggle: (id: string, enabled: boolean) => void;
  onSelect: (id: string) => void;
}) {
  const total = z.total ?? z.dur * 60;
  const progress = z.state === "watering" ? clamp(1 - (z.remaining ?? 0) / total, 0, 1) : 0;
  const active = z.state === "watering" || z.state === "queued"; // stoppable
  const canRun = z.enabled && !active;

  const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12.5,
    fontWeight: 600,
    padding: "7px 14px",
    borderRadius: "var(--radius-btn)",
  } as const;

  return (
    <div
      onClick={() => onSelect(z.id)}
      className="card-in zone-card"
      style={{ ...card, padding: 18, cursor: "pointer" }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div
            className="serif"
            style={{
              fontFamily: FONT.serif,
              fontSize: 17,
              fontWeight: 500,
              color: C.ink,
              lineHeight: 1.15,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {z.name}
          </div>
          <div
            style={{
              marginTop: 2,
              fontSize: 11,
              letterSpacing: ".06em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ color: C.muted }}>{z.type === "drip" ? "DRIP" : "SPRAY"}</span>{" "}
            <span style={{ color: C.hoverBorder }}>·</span>{" "}
            <span style={{ color: C.mutedDeep }}>{z.plant}</span>
          </div>
        </div>
        <Pill z={z} />
      </div>

      {/* Body */}
      <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
        <Reservoir z={z} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
            <span
              className="serif tnum"
              style={{
                fontFamily: FONT.serif,
                fontSize: 33,
                fontWeight: 400,
                color: C.ink,
                lineHeight: 0.85,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {Math.round(z.moisture)}
            </span>
            <span style={{ color: C.muted, fontSize: 12.5 }}>% soil</span>
            <span style={{ alignSelf: "center", marginLeft: 1 }}>
              <SourceDot source={soilSrc} fallback="Model (ET-driven) · simulated" />
            </span>
          </div>

          <div style={{ fontSize: 11.5, color: C.textMid, marginTop: 7 }}>
            Target{" "}
            <span className="mono" style={{ fontFamily: FONT.mono, color: C.sage }}>
              {z.band[0]}–{z.band[1]}%
            </span>{" "}
            · refill at{" "}
            <span className="mono" style={{ fontFamily: FONT.mono, color: C.beacon }}>
              {z.mad}%
            </span>
          </div>

          {z.state === "watering" ? (
            <div style={{ marginTop: 13 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: 11.5,
                  color: C.textMid,
                }}
              >
                <span>{mmss(z.remaining ?? 0)} left</span>
                <span className="mono" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FONT.mono, color: C.sea }}>
                  {z.flow.toFixed(1)} L/min
                  <SourceDot source={flowSrc} fallback="Simulated" />
                </span>
              </div>
              <div
                style={{
                  marginTop: 7,
                  height: 6,
                  borderRadius: 99,
                  background: C.track,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${progress * 100}%`,
                    background: `linear-gradient(90deg, ${C.seaDeep}, ${C.ice})`,
                    transition: "width 1s linear",
                  }}
                />
              </div>
            </div>
          ) : (
            <div
              style={{
                marginTop: 13,
                fontSize: 11.5,
                color: C.textMid,
              }}
            >
              {paused && z.enabled ? (
                <span style={{ color: C.ice }}>Auto paused — rain delay</span>
              ) : z.state === "queued" ? (
                <span style={{ color: C.sea }}>Queued — starts when the line is free</span>
              ) : (
                <>
                  <span style={{ color: C.ink }}>{z.nextIn}</span> · {z.nextReason}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginTop: 16,
          paddingTop: 14,
          borderTop: `1px solid ${C.lineSoft}`,
        }}
      >
        <Stepper value={z.dur} onChange={(v) => onStep(z.id, v)} />
        <div
          style={{ display: "flex", alignItems: "center", gap: 8 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => onToggle(z.id, !z.enabled)}
            style={{
              fontSize: 12,
              fontWeight: 500,
              padding: "7px 11px",
              borderRadius: "var(--radius-btn)",
              color: C.textMid,
              background: "transparent",
              border: `1px solid ${C.hairline}`,
              cursor: "pointer",
            }}
          >
            {z.enabled ? "Disable" : "Enable"}
          </button>

          {active ? (
            <button
              disabled={busyStop}
              onClick={() => onStop(z.id)}
              aria-label={`Stop ${z.name}`}
              style={{
                ...btnBase,
                color: C.onAccent,
                background: C.coral,
                border: `1px solid ${C.coral}`,
                cursor: busyStop ? "wait" : "pointer",
                opacity: busyStop ? 0.7 : 1,
              }}
            >
              {busyStop ? <Loader2 size={13} className="spin" /> : <Square size={12} fill={C.onAccent} />}
              {busyStop ? "Stopping…" : "Stop"}
            </button>
          ) : (
            <button
              disabled={!canRun || busyRun}
              onClick={() => onRun(z.id, z.dur)}
              aria-label={`Run ${z.name} for ${z.dur} minutes`}
              style={{
                ...btnBase,
                color: canRun ? C.onAccent : C.textLow,
                background: canRun ? C.seaDeep : C.track,
                border: `1px solid ${canRun ? C.seaDeep : C.hairline}`,
                cursor: !canRun ? "not-allowed" : busyRun ? "wait" : "pointer",
                opacity: busyRun ? 0.7 : 1,
              }}
            >
              {busyRun ? <Loader2 size={13} className="spin" /> : <Play size={13} fill={canRun ? C.onAccent : "none"} />}
              {busyRun ? "Starting…" : "Run"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
